import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from 'primereact/dropdown';
import RoleService from '../../services/user-role/role-service';
import orderServices from '../../services/orders/orderservice';

interface AssignRoleSearchProps {
    onSearch: (selectedUser: number | null, selectedRole: number | null) => void;
    onAddClick: () => void;
    onReset: () => void;
}

interface UserOption {
    userID: number;
    name: string;
    primaryContactNbr: string;
    detail?: string;  // FIXED: Add detail field
}

const AssignRoleSearch: React.FC<AssignRoleSearchProps> = ({ onSearch, onAddClick, onReset }) => {
    const [selectedUser, setSelectedUser] = useState<number | null>(null);
    const [selectedRole, setSelectedRole] = useState<number | null>(null);

    const [userOptions, setUserOptions] = useState<UserOption[]>([]);
    const [roleOptions, setRoleOptions] = useState<any[]>([]);

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        loadRoles();
    }, []);

    const loadRoles = async () => {
        try {
            const data = await RoleService.getAllRoles();
            // FIXED: Ensure proper role structure
            const formattedRoles = Array.isArray(data) ? data : [];
            setRoleOptions(formattedRoles);
        } catch (error) {
            console.error("Error loading roles", error);
            setRoleOptions([]);
        }
    };

    // FIXED: Complete fix for user search dropdown
    const handleUserFilter = (e: DropdownFilterEvent) => {
        const query = (e.filter as string)?.trim();
        console.log("User filter query:", query);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        // FIXED: Only search if query has 2+ characters
        if (query && query.length >= 2) {
            debounceTimeout.current = setTimeout(async () => {
                try {
                    const response = await orderServices.getOrderUsers(query);
                    console.log("User search response:", response);

                    if (response && Array.isArray(response) && response.length > 0) {
                        // FIXED: Proper formatting matching optionLabel="detail"
                        const formattedUsers: UserOption[] = response.map((user: any) => ({
                            userID: user.userID || user.userId,
                            name: user.name || '',
                            primaryContactNbr: user.primaryContactNbr || '',
                            detail: `${user.name || ''}${user.primaryContactNbr ? `, ${user.primaryContactNbr}` : ''}`.trim()
                        }));
                        setUserOptions(formattedUsers);
                    } else {
                        setUserOptions([]);
                    }
                } catch (error) {
                    console.error("Error filtering users:", error);
                    setUserOptions([]);
                }
            }, 500); // Increased debounce for better UX
        } else {
            // Clear options if query too short
            setUserOptions([]);
        }
    };

    const handleSearch = () => {
        console.log("Search clicked:", { selectedUser, selectedRole });
        onSearch(selectedUser, selectedRole);
    };

    const handleReset = () => {
        setSelectedUser(null);
        setSelectedRole(null);
        setUserOptions([]);
        onReset();
    };

    // FIXED: Add empty state placeholder
    const emptyUserOptions: UserOption[] = [{
        userID: 0,
        name: 'Type to search users...',
        primaryContactNbr: '',
        detail: 'Type to search users...'
    }];

    return (
        <div className="assignment-filter-bar">
            <div className="assignment-filter-grid">
                <div className="assignment-filter-field">
                    <label className="assignment-filter-label">
                        <i className="pi pi-user"></i>
                        USER
                    </label>
                    <Dropdown
                        value={selectedUser}
                        options={userOptions.length > 0 ? userOptions : emptyUserOptions}
                        onChange={(e: DropdownChangeEvent) => {
                            console.log("User selected:", e.value);
                            setSelectedUser(e.value);
                        }}
                        onFilter={handleUserFilter}
                        filter
                        filterPlaceholder="Type user name or mobile..."
                        optionLabel="detail"
                        optionValue="userID"
                        placeholder="Search for User"
                        className="assignment-filter-dropdown w-full"
                        showClear
                        emptyFilterMessage="No users found. Try different keywords."
                        emptyMessage="Type 2+ characters to search users"
                    />
                </div>
                <div className="assignment-filter-field">
                    <label className="assignment-filter-label">
                        <i className="pi pi-shield"></i>
                        ROLE
                    </label>
                    <Dropdown
                        value={selectedRole}
                        options={roleOptions}
                        onChange={(e: DropdownChangeEvent) => {
                            console.log("Role selected:", e.value);
                            setSelectedRole(e.value);
                        }}
                        optionLabel="name"
                        optionValue="roleId"
                        placeholder="Select Role"
                        className="assignment-filter-dropdown w-full"
                        showClear
                    />
                </div>
                <div className="assignment-filter-actions">
                    <Button
                        label="Search"
                        icon="pi pi-search"
                        onClick={handleSearch}
                        className="assignment-search-btn"
                    // disabled={!selectedUser && !selectedRole}
                    />
                    <Button
                        label="Reset"
                        icon="pi pi-refresh"
                        onClick={handleReset}
                        className="assignment-reset-btn"
                    />
                    <Button
                        label="Assign Role"
                        icon="pi pi-plus-circle"
                        onClick={onAddClick}
                        className="assignment-assign-btn"
                    />
                </div>
            </div>
        </div>
    );
};

export default AssignRoleSearch;
